import { ANALYSIS_FRAME_SIZE, Analyzer, dspConfig, setDspConfig, saveDspConfig, informationObjective, METRICS, SideStats, } from "@beatbox/voice-core";
const WINDOW_HALF = 0.16;
const CROSS_SECONDS = 2.2;
// Raw-frame tagging for captured audio. Out-of-window frames are the "quiet"
// reference and continuously update a running ambient floor. In-window
// frames count as "voice" when they are clearly louder than that measured
// floor (a RELATIVE test), so calibration works even for quiet mics whose
// raw speech never exceeds a fixed absolute threshold — the exact case that
// previously caused all voice frames to be silently ignored.
const RAW_SIGNAL_FLOOR = 0.012; // fallback floor before ambient is known
const ABS_MIN_SIGNAL_RMS = 0.0008; // guard so silence/DC is never "signal"
const SIGNAL_OVER_AMBIENT = 2.5; // in-window must beat ambient floor by this
const MAX_FRAMES = 900;
const MIN_SIDE_FRAMES = 30;
// Number of ball cycles to collect before auto-accepting the derived
// parameters and moving on to the next setup step.
// With the raw-audio capture approach a single full pass through the window
// (voice) and out of it (quiet) gives enough tagged frames to optimize.
const AUTO_ACCEPT_CYCLES = 2;
const OPTIMIZABLE = [
    {
        key: "energyScale",
        label: "Loudness gain",
        metric: "energy",
        min: 0.5,
        max: 40,
        gridN: 14,
        log: true,
        round: (v) => Math.round(v * 2) / 2,
        objective: {
            targetSeparability: 2.5,
            separabilityWeight: 1,
            infoWeight: 0.8,
            infoRef: 0.22,
            saturationWeight: 1.2,
            magnitudeWeight: 0.2,
            magnitudeRef: 10,
        },
    },
    {
        key: "transientScale",
        label: "Onset sensitivity",
        metric: "transient",
        min: 1,
        max: 12,
        gridN: 12,
        log: true,
        round: (v) => Math.round(v * 2) / 2,
        objective: {
            targetSeparability: 1.6,
            separabilityWeight: 1,
            infoWeight: 0.9,
            infoRef: 0.2,
            saturationWeight: 1,
            magnitudeWeight: 0.25,
            magnitudeRef: 6,
        },
    },
];
export class AcGainPanel extends HTMLElement {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "raf", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "running", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "startTs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "ballX", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0.5
        });
        Object.defineProperty(this, "inWindow", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "lastPhase", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "cycleCount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "rec", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "liveEnergy", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "autoAccepted", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        // Raw-audio capture.
        Object.defineProperty(this, "captureCtx", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "captureSource", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "scriptNode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "silentSink", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "ring", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Float32Array(ANALYSIS_FRAME_SIZE)
        });
        Object.defineProperty(this, "ringFill", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "frames", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "sr", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 48000
        });
        // Running estimate of the out-of-window (quiet) RMS, used as a relative
        // reference so in-window voice is detected regardless of raw mic level.
        Object.defineProperty(this, "ambientRms", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "ambientInit", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
    }
    connectedCallback() {
        this.style.position = "fixed";
        this.style.inset = "0";
        this.style.zIndex = "1100";
        this.style.pointerEvents = "auto";
        this.render();
        this.start();
    }
    disconnectedCallback() {
        this.stop();
        this.stopCapture();
    }
    /**
     * Provide the live mic AudioContext + source node so the panel can tap
     * RAW audio. Raw samples are config-independent, which is exactly what
     * lets us later re-run the DSP over them under many candidate parameter
     * sets and pick the best offline.
     */
    setSource(ctx, source) {
        this.captureCtx = ctx;
        this.captureSource = source;
        this.sr = ctx.sampleRate;
        this.startCapture();
    }
    update(snap) {
        this.liveEnergy = snap.features?.energy ?? 0;
    }
    startCapture() {
        if (!this.captureCtx || !this.captureSource)
            return;
        if (this.scriptNode)
            return;
        const ctx = this.captureCtx;
        if (typeof ctx.createScriptProcessor !== "function") {
            console.warn("[ac-gain-panel] ScriptProcessorNode unavailable; cannot " +
                "capture raw audio for offline optimization");
            return;
        }
        const bufSize = 1024;
        const script = ctx.createScriptProcessor(bufSize, 1, 1);
        script.onaudioprocess = (e) => this.onAudio(e.inputBuffer.getChannelData(0));
        const sink = ctx.createGain();
        sink.gain.value = 0;
        this.captureSource.connect(script);
        script.connect(sink).connect(ctx.destination);
        this.scriptNode = script;
        this.silentSink = sink;
        console.info("[ac-gain-panel] raw-audio capture started " +
            `(sampleRate=${this.sr}, frame=${ANALYSIS_FRAME_SIZE})`);
    }
    stopCapture() {
        try {
            if (this.scriptNode) {
                this.scriptNode.onaudioprocess = null;
                this.captureSource?.disconnect(this.scriptNode);
                this.scriptNode.disconnect();
            }
            this.silentSink?.disconnect();
        }
        catch { }
        this.scriptNode = null;
        this.silentSink = null;
    }
    onAudio(ch) {
        for (let i = 0; i < ch.length; i++) {
            this.ring[this.ringFill++] = ch[i];
            if (this.ringFill >= ANALYSIS_FRAME_SIZE) {
                this.ringFill = 0;
                this.pushFrame(this.ring);
            }
        }
    }
    pushFrame(ring) {
        let s = 0;
        for (let i = 0; i < ring.length; i++)
            s += ring[i] * ring[i];
        const rms = Math.sqrt(s / ring.length);
        let tag = "none";
        if (this.inWindow) {
            // Relative test: voice if clearly above the measured ambient
            // floor. Before any ambient has been seen, fall back to a low
            // absolute floor. This makes quiet mics calibratable.
            const floor = this.ambientInit ? this.ambientRms : RAW_SIGNAL_FLOOR;
            const need = Math.max(floor * SIGNAL_OVER_AMBIENT, ABS_MIN_SIGNAL_RMS);
            if (rms > need)
                tag = "signal";
        }
        else {
            // Out of window: this is the quiet reference. Track a running
            // ambient floor and always tag as ambient so the optimizer has a
            // baseline distribution to contrast the voice frames against.
            this.ambientRms = this.ambientInit
                ? this.ambientRms * 0.9 + rms * 0.1
                : rms;
            this.ambientInit = true;
            tag = "ambient";
        }
        this.frames.push({
            samples: ring.slice(0, ANALYSIS_FRAME_SIZE),
            tag,
        });
        if (this.frames.length > MAX_FRAMES) {
            this.frames.splice(0, this.frames.length - MAX_FRAMES);
        }
    }
    start() {
        this.running = true;
        this.startTs =
            typeof performance !== "undefined" ? performance.now() : Date.now();
        console.info("[ac-gain-panel] session START — capturing RAW audio tagged as " +
            "voice/quiet by the bouncing ball. Parameters are optimized " +
            "OFFLINE by re-running the DSP over the raw audio under many " +
            "candidate settings, maximizing silence↔voice separability AND " +
            "the information content (spread) of each metric while avoiding " +
            "saturation.");
        const loop = () => {
            if (!this.running)
                return;
            this.tick();
            this.raf = requestAnimationFrame(loop);
        };
        this.raf = requestAnimationFrame(loop);
    }
    stop() {
        this.running = false;
        cancelAnimationFrame(this.raf);
        this.raf = 0;
    }
    tick() {
        const now = typeof performance !== "undefined" ? performance.now() : Date.now();
        const t = (now - this.startTs) / 1000;
        const period = CROSS_SECONDS * 2;
        const phase = (t % period) / period;
        this.ballX = phase < 0.5 ? phase * 2 : 2 - phase * 2;
        this.inWindow = Math.abs(this.ballX - 0.5) <= WINDOW_HALF;
        if (phase < this.lastPhase)
            this.onCycleComplete();
        this.lastPhase = phase;
        this.draw(now);
    }
    onCycleComplete() {
        this.cycleCount += 1;
        this.optimize();
        this.updateReadout();
        if (!this.autoAccepted &&
            this.rec &&
            this.cycleCount >= AUTO_ACCEPT_CYCLES) {
            this.autoAccepted = true;
            console.info("[ac-gain-panel] auto-accepting derived parameters after " +
                `${this.cycleCount} cycle(s)`);
            this.applyRecommendation();
        }
    }
    grid(p) {
        const out = [];
        for (let i = 0; i < p.gridN; i++) {
            const t = p.gridN <= 1 ? 0 : i / (p.gridN - 1);
            const v = p.log
                ? p.min * Math.pow(p.max / p.min, t)
                : p.min + (p.max - p.min) * t;
            out.push(p.round(v));
        }
        return [...new Set(out)];
    }
    countTag(tag) {
        let n = 0;
        for (const f of this.frames)
            if (f.tag === tag)
                n++;
        return n;
    }
    /**
     * Re-run the DSP over ALL captured raw frames (in temporal order, so
     * onset/flux state is correct) under `cfg`, collecting `metric` into
     * voice/quiet distributions.
     */
    reextract(cfg, metric) {
        const an = new Analyzer(this.sr, cfg);
        const inS = new SideStats();
        const outS = new SideStats();
        for (const fr of this.frames) {
            const f = an.extract(fr.samples);
            const v = metric(f);
            if (fr.tag === "signal")
                inS.push(v);
            else if (fr.tag === "ambient")
                outS.push(v);
        }
        return { inS, outS };
    }
    finalStats(cfg) {
        const an = new Analyzer(this.sr, cfg);
        const eIn = new SideStats();
        const eOut = new SideStats();
        const vIn = new SideStats();
        for (const fr of this.frames) {
            const f = an.extract(fr.samples);
            if (fr.tag === "signal") {
                eIn.push(f.energy ?? 0);
                vIn.push(f.voicing ?? 0);
            }
            else if (fr.tag === "ambient") {
                eOut.push(f.energy ?? 0);
            }
        }
        return { eIn, eOut, vIn };
    }
    optimize() {
        const sig = this.countTag("signal");
        const amb = this.countTag("ambient");
        if (sig < MIN_SIDE_FRAMES || amb < MIN_SIDE_FRAMES) {
            this.rec = null;
            return;
        }
        const base = { ...dspConfig };
        const chosen = {};
        const rationale = [];
        const params = [];
        // Coordinate descent: a couple of passes let the two parameters
        // co-adapt (each pass re-optimizes one holding the other fixed).
        const ROUNDS = 2;
        for (let round = 0; round < ROUNDS; round++) {
            for (const p of OPTIMIZABLE) {
                const metric = METRICS[p.metric] ?? ((f) => f[p.metric] ?? 0);
                let best = null;
                for (const cand of this.grid(p)) {
                    const cfg = {
                        ...base,
                        ...chosen,
                        [p.key]: cand,
                    };
                    const { inS, outS } = this.reextract(cfg, metric);
                    if (inS.n < 5 || outS.n < 5)
                        continue;
                    const obj = informationObjective(inS, outS, cand, p.objective);
                    if (!best || obj.score > best.obj.score) {
                        best = { value: cand, obj, inS, outS };
                    }
                }
                if (!best)
                    continue;
                chosen[p.key] = best.value;
                if (round === ROUNDS - 1) {
                    params.push({
                        key: p.key,
                        label: p.label,
                        value: best.value,
                    });
                    rationale.push(`${p.label} → ${best.value}: best offline trade-off ` +
                        `on '${p.metric}' ` +
                        `(separability≈${best.obj.sep.toFixed(2)}, ` +
                        `info/σ≈${best.inS.sigma.toFixed(2)}, ` +
                        `voice μ≈${best.inS.mu.toFixed(2)}, ` +
                        `quiet μ≈${best.outS.mu.toFixed(3)}).`);
                    if (best.inS.mu > 0.95 && best.inS.sigma < 0.03) {
                        rationale.push(`⚠ ${p.label}: '${p.metric}' saturates near 1.0 ` +
                            `even at the best value — lower input volume or ` +
                            `use a quieter room to recover dynamic range.`);
                    }
                }
            }
        }
        const finalCfg = { ...base, ...chosen };
        const { eIn, eOut, vIn } = this.finalStats(finalCfg);
        const thresholds = [];
        if (eIn.n >= 6 && eOut.n >= 6) {
            let gate = eOut.mu + Math.max(eOut.sigma * 3, 0.01);
            const ceiling = eIn.mu * 0.6;
            if (ceiling > 0 && gate > ceiling)
                gate = ceiling;
            gate = Math.max(0.01, Math.min(0.3, gate));
            gate = Math.round(gate * 1000) / 1000;
            const headroom = eIn.mu - gate;
            thresholds.push({
                key: "energyGate",
                label: "Silence gate",
                value: gate,
                note: `${headroom > 0.15 ? "good" : "tight"} headroom ` +
                    `(${headroom.toFixed(3)}) below voice`,
            });
            rationale.push(`Silence gate ${gate} sits above ambient ` +
                `(μ=${eOut.mu.toFixed(3)}) and below voice ` +
                `(μ=${eIn.mu.toFixed(3)}).`);
        }
        if (vIn.n >= 6) {
            let vg = Math.max(0, vIn.mu - vIn.sigma);
            vg = Math.max(0.05, Math.min(0.9, vg));
            vg = Math.round(vg * 100) / 100;
            thresholds.push({
                key: "voicingGateForPitch",
                label: "Pitch voicing gate",
                value: vg,
                note: `one σ below in-window voicing ` + `(μ=${vIn.mu.toFixed(2)})`,
            });
        }
        rationale.push(`Optimized offline over ${this.frames.length} raw frame(s) ` +
            `(${sig} voice / ${amb} quiet). Keep making sound in the window ` +
            `and staying quiet outside it to sharpen the fit.`);
        this.rec = { params, thresholds, rationale };
    }
    draw(now) {
        void now;
        const canvas = this.querySelector("[data-gain-canvas]");
        if (!canvas)
            return;
        const ctx = canvas.getContext("2d");
        if (!ctx)
            return;
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        const cw = canvas.clientWidth || 520;
        const ch = canvas.clientHeight || 120;
        if (canvas.width !== Math.floor(cw * dpr) ||
            canvas.height !== Math.floor(ch * dpr)) {
            canvas.width = Math.floor(cw * dpr);
            canvas.height = Math.floor(ch * dpr);
        }
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, cw, ch);
        const pad = 24;
        const trackY = ch / 2;
        const trackW = cw - pad * 2;
        const x0 = pad;
        ctx.strokeStyle = "rgba(255,255,255,0.15)";
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(x0, trackY);
        ctx.lineTo(x0 + trackW, trackY);
        ctx.stroke();
        const winCx = x0 + trackW * 0.5;
        const winW = trackW * (WINDOW_HALF * 2);
        ctx.fillStyle = this.inWindow
            ? "rgba(74,222,128,0.18)"
            : "rgba(34,211,238,0.14)";
        ctx.fillRect(winCx - winW / 2, pad, winW, ch - pad * 2);
        ctx.strokeStyle = this.inWindow
            ? "rgba(74,222,128,0.7)"
            : "rgba(34,211,238,0.5)";
        ctx.lineWidth = 2;
        ctx.strokeRect(winCx - winW / 2, pad, winW, ch - pad * 2);
        ctx.fillStyle = "rgba(255,255,255,0.55)";
        ctx.font = "600 12px system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(this.inWindow ? "SPEAK / HUM / POP" : "STAY QUIET", winCx, pad - 8);
        const bx = x0 + trackW * this.ballX;
        const color = this.inWindow ? "#4ade80" : "#22d3ee";
        ctx.shadowColor = color;
        ctx.shadowBlur = this.inWindow ? 22 : 10;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(bx, trackY, 14, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
    }
    updateReadout() {
        const el = this.querySelector("[data-gain-readout]");
        if (el) {
            const sig = this.countTag("signal");
            const amb = this.countTag("ambient");
            el.innerHTML =
                `<span style="color:#4ade80">voice frames ${sig}</span>` +
                    ` &nbsp;·&nbsp; <span style="opacity:.7">quiet frames ` +
                    `${amb}</span>` +
                    ` &nbsp;·&nbsp; <span style="opacity:.6">live energy ` +
                    `${this.liveEnergy.toFixed(3)}</span>`;
        }
        const advice = this.querySelector("[data-gain-advice]");
        if (advice) {
            if (!this.rec) {
                advice.innerHTML =
                    `<span style="opacity:.7">Collecting raw audio… ` +
                        `speak/hum/pop in the window and stay quiet outside it ` +
                        `until advice appears.</span>`;
            }
            else {
                const cur = (label, v) => `<div style="display:flex;justify-content:space-between;
                                gap:12px;font-size:12px;padding:2px 0">
                               <span style="opacity:.8">${label}</span>
                               <span><b>${v}</b></span>
                             </div>`;
                const paramRows = this.rec.params
                    .map((p) => cur(p.label, p.value))
                    .join("");
                const threshRows = this.rec.thresholds
                    .map((t) => cur(t.label, t.value))
                    .join("");
                advice.innerHTML =
                    paramRows +
                        threshRows +
                        `<ul style="margin:8px 0 0;padding-left:18px;font-size:11px;
                                opacity:.8;line-height:1.5">` +
                        this.rec.rationale.map((r) => `<li>${r}</li>`).join("") +
                        `</ul>`;
            }
        }
        const apply = this.querySelector("[data-gain-apply]");
        if (apply) {
            apply.disabled = !this.rec;
            apply.style.opacity = this.rec ? "1" : ".5";
        }
    }
    applyRecommendation() {
        if (!this.rec)
            return;
        console.info("[ac-gain-panel] APPLYING offline-optimized recommendation from " +
            `${this.frames.length} raw frame(s) across ` +
            `${this.cycleCount} cycle(s)`);
        const patch = {};
        for (const p of this.rec.params) {
            patch[String(p.key)] = p.value;
        }
        for (const t of this.rec.thresholds) {
            patch[String(t.key)] = t.value;
        }
        setDspConfig(patch);
        void saveDspConfig(patch);
        console.info("[ac-gain-panel] applied calibration", patch);
        this.dispatchEvent(new CustomEvent("gainpanel-applied", { bubbles: true }));
        this.dispatchEvent(new CustomEvent("gainpanel-done", { bubbles: true }));
    }
    render() {
        this.innerHTML = `
                  <div style="position:absolute;inset:0;background:rgba(0,0,0,.6);
                      display:flex;align-items:center;justify-content:center">
                    <div style="background:#0d1024;border:1px solid #2a2f4a;border-radius:16px;
                        width:min(92vw,560px);color:#fff;padding:20px 22px">
                      <div style="display:flex;align-items:center;gap:10px;margin-bottom:8px">
                        <span style="font-size:20px">🎚️</span>
                        <span style="flex:1;font-size:18px;font-weight:800">Signal Calibration</span>
                        <button data-gain-cancel
                            style="pointer-events:auto;cursor:pointer;padding:6px 12px;
                            border-radius:8px;border:1px solid #f87171;background:#1a1f3a;
                            color:#fff;font-size:13px;font-weight:600">Cancel</button>
                      </div>
                      <p style="opacity:.8;font-size:13px;margin:0 0 14px;line-height:1.5">
                        Speak, hum, or pop steadily while the ball is inside the
                        highlighted window, and <b>stay quiet</b> while it is outside.
                        We record your <b>raw audio</b> tagged as voice/quiet, then
                        replay it through the DSP under many candidate settings to
                        find the parameters that best separate voice from silence
                        <i>and</i> keep the most information in each metric — without
                        over-driving into saturation.
                      </p>
                      <canvas data-gain-canvas
                          style="display:block;width:100%;height:120px;border-radius:12px;
                          background:radial-gradient(circle at 50% 50%,rgba(34,211,238,.08),rgba(10,12,24,.9))"></canvas>
                      <div data-gain-readout style="text-align:center;margin-top:12px;
                          font-size:12px;font-weight:600;line-height:1.8">
                        <span style="opacity:.7">Collecting…</span>
                      </div>
                      <div data-gain-advice style="margin-top:14px;padding:12px;
                          border-radius:10px;background:rgba(18,22,42,.6);
                          border:1px solid #2a2f4a;min-height:48px">
                        <span style="opacity:.7">Collecting raw audio… speak/hum/pop
                          in the window and stay quiet outside it until advice
                          appears.</span>
                      </div>
                      <div style="display:flex;justify-content:space-between;gap:10px;margin-top:16px">
                        <button data-gain-reset
                            style="pointer-events:auto;cursor:pointer;padding:8px 14px;
                            border-radius:10px;border:1px solid #22d3ee;background:#1a1f3a;
                            color:#fff;font-size:13px;font-weight:600">Restart sampling</button>
                        <button data-gain-apply disabled
                            style="pointer-events:auto;cursor:pointer;padding:8px 16px;
                            border-radius:10px;border:1px solid #4ade80;background:#1a1f3a;
                            color:#fff;font-size:14px;font-weight:700;opacity:.5">
                            Apply recommendation</button>
                      </div>
                    </div>
                  </div>`;
        this.querySelector("[data-gain-cancel]")?.addEventListener("click", () => {
            this.dispatchEvent(new CustomEvent("gainpanel-cancel", { bubbles: true }));
        });
        this.querySelector("[data-gain-reset]")?.addEventListener("click", () => {
            this.frames = [];
            this.ringFill = 0;
            this.ambientRms = 0;
            this.ambientInit = false;
            this.rec = null;
            this.cycleCount = 0;
            this.updateReadout();
            console.info("[ac-gain-panel] sampling restarted");
        });
        this.querySelector("[data-gain-apply]")?.addEventListener("click", () => this.applyRecommendation());
    }
}
customElements.define("ac-gain-panel", AcGainPanel);
